import {
    arrow,
    autoUpdate,
    flip,
    FloatingArrow,
    FloatingFocusManager,
    FloatingNode,
    FloatingPortal,
    FloatingTree,
    offset,
    type Placement,
    safePolygon,
    shift,
    useDismiss,
    useFloating,
    useFloatingNodeId,
    useFloatingParentNodeId,
    useFocus,
    useHover,
    useInteractions,
    useMergeRefs,
    useRole,
} from '@floating-ui/react'
import { AnimatePresence, motion } from 'motion/react'
import { cloneElement, HTMLAttributes, isValidElement, ReactNode, Ref, useRef, useState } from 'react'

import semanticColors from '../../tokens/semanticColors.json'
import { astraTwMerge } from '../Utilities/astraTwMerge'
import { FLOATING_MOTION_PROPS, TRANSFORM_ORIGIN_BY_PLACEMENT } from '../Utilities/floatingMotion'

type Variant = 'inverted' | 'default'
export type HoverCardContentProps = HTMLAttributes<HTMLDivElement>

type Props = {
    /*
     * The controlled open state of the hover card. Must be used in conjunction with onOpenChange.
     */
    open?: boolean
    /*
     * Event handler called when the open state of the hover card changes.
     */
    onOpenChange?: (open: boolean) => void
    /*
     * The duration from when the mouse enters the trigger until the hover card opens. Defaults to 600ms.
     */
    openDelay?: number
    /*
     * The duration from when the mouse leaves the trigger or content until the hover card closes. Defaults
     * to 200ms, which is what lets the pointer travel into the card.
     */
    closeDelay?: number
    /*
     * The trigger element. If using a React component, it must spread props and forward ref.
     */
    children: ReactNode
    /*
     * The content to display in the hover card.
     */
    hoverContent: ReactNode
    /*
     * If `true`, the default padding is removed from the hover card content.
     */
    noPadding?: boolean
    /**
     * Floating UI placement relative to the trigger.
     */
    placement?: Placement
    /*
     * sideOffset is used to control the offset in pixels from the side of the trigger.
     */
    sideOffset?: number
    /**
     * An offset in pixels along the alignment axis of an aligned placement.
     */
    alignOffset?: number
    /*
     * showArrow is used to control the visibility of the arrow on the hover card.
     */
    showArrow?: boolean

    variant?: Variant
    /*
     * Disables animations for the hover card.
     */
    reducedMotion?: boolean
    /*
     * Specify a container element to portal the content into.
     */
    container?: HTMLElement | null
} & HoverCardContentProps

type TriggerElementProps = {
    ref?: Ref<Element>
    [key: string]: unknown
}

const containerStyles: Record<Variant, string> = {
    default: 'border border-base bg-surface-base',
    inverted: 'bg-surface-inverted text-inverted',
}

const colors = semanticColors['mode-1']
const arrowStyles: Record<Variant, { fill: string; stroke?: string; strokeWidth?: number }> = {
    default: {
        fill: colors['bg-surface-base'],
        stroke: colors['border-base'],
        strokeWidth: 1,
    },
    inverted: {
        fill: colors['bg-surface-inverted'],
    },
}

const ARROW_HEIGHT = 7
const ARROW_GAP = 3
const CONTENT_GAP = 4

const AstraHoverCardImpl = ({
    open,
    onOpenChange,
    openDelay = 600,
    closeDelay = 200,
    children,
    hoverContent,
    noPadding = false,
    placement = 'bottom',
    sideOffset = 0,
    alignOffset = 0,
    showArrow,
    variant = 'default',
    reducedMotion,
    container,
    className,
    style,
    onClick,
    ...rest
}: Props) => {
    const isControlled = open !== undefined
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
    const currentOpen = isControlled ? open : uncontrolledOpen

    const setOpen = (next: boolean) => {
        if (!isControlled) setUncontrolledOpen(next)
        onOpenChange?.(next)
    }

    const arrowRef = useRef<SVGSVGElement | null>(null)

    // FloatingTree integration: get a unique node ID for this hover card
    const nodeId = useFloatingNodeId()

    const {
        refs,
        context,
        floatingStyles,
        placement: resolvedPlacement,
    } = useFloating({
        nodeId,
        open: currentOpen,
        onOpenChange: setOpen,
        placement,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset({
                mainAxis: sideOffset + (showArrow ? ARROW_HEIGHT + ARROW_GAP : CONTENT_GAP),
                alignmentAxis: alignOffset,
            }),
            flip(),
            shift({ padding: 8 }),
            showArrow ? arrow({ element: arrowRef, padding: 8 }) : null,
        ],
    })
    const hover = useHover(context, {
        delay: reducedMotion ? 0 : { open: openDelay, close: closeDelay },
        handleClose: safePolygon({ requireIntent: true, buffer: 1 }),
        move: false,
    })
    const focus = useFocus(context)
    const dismiss = useDismiss(context, {
        ancestorScroll: false,
        bubbles: {
            outsidePress: true,
        },
    })
    const role = useRole(context, { role: 'dialog' })

    const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role])

    const contentStyles = astraTwMerge(
        'relative rounded-xl shadow-hovercard outline-none [&>span]:-z-elevated',
        TRANSFORM_ORIGIN_BY_PLACEMENT[resolvedPlacement],
        containerStyles[variant],
        noPadding ? '' : 'p-3',
        className
    )

    const triggerRef = isValidElement<TriggerElementProps>(children) ? children.props.ref : undefined
    const setTriggerRef = useMergeRefs<Element>([refs.setReference, triggerRef])

    if (!isValidElement<TriggerElementProps>(children)) return <>{children}</>

    const referenceProps = getReferenceProps(children.props)

    const portalRoot =
        container ?? ((refs.reference.current as HTMLElement | null)?.closest('[role="dialog"],[role="alertdialog"]') as HTMLElement | null) ?? undefined

    return (
        <FloatingNode id={nodeId}>
            {cloneElement(children, {
                ...referenceProps,
                'data-test': children.props['data-test'] ?? 'hero-hover-card-trigger',
                ref: setTriggerRef,
            })}

            <AnimatePresence>
                {currentOpen && (
                    <FloatingPortal root={portalRoot}>
                        <FloatingFocusManager context={context} initialFocus={-1} modal={false}>
                            <div
                                ref={refs.setFloating}
                                className="z-elevated"
                                style={{
                                    ...floatingStyles,
                                    ...style,
                                }}
                                {...getFloatingProps(rest)}
                            >
                                <motion.div
                                    className={contentStyles}
                                    role="presentation"
                                    {...(reducedMotion ? {} : FLOATING_MOTION_PROPS)}
                                    onClick={e => {
                                        onClick?.(e)
                                        e.stopPropagation()
                                    }}
                                >
                                    {hoverContent}

                                    {showArrow && <FloatingArrow ref={arrowRef} context={context} height={ARROW_HEIGHT} {...arrowStyles[variant]} />}
                                </motion.div>
                            </div>
                        </FloatingFocusManager>
                    </FloatingPortal>
                )}
            </AnimatePresence>
        </FloatingNode>
    )
}

/**
 * AstraHoverCard wraps the implementation in a FloatingTree if it's the root element.
 * This enables proper coordination between nested floating elements (hover cards, popovers).
 */
export const AstraHoverCard = (props: Props) => {
    const parentId = useFloatingParentNodeId()

    // If there's no parent FloatingTree, wrap in one (this is the root)
    if (parentId === null) {
        return (
            <FloatingTree>
                <AstraHoverCardImpl {...props} />
            </FloatingTree>
        )
    }

    // Otherwise, just render the implementation (we're already inside a FloatingTree)
    return <AstraHoverCardImpl {...props} />
}
